import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { User, Phone, MapPin, Edit3, Save, Plus, Trash2, Check, Package, Mail, Info } from "lucide-react";

const Profile = () => {
  const { t } = useLang();
  const { user, refresh, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isWelcome = params.get("welcome") === "1";
  const [editing, setEditing] = useState(isWelcome);
  const [form, setForm] = useState({ full_name: "", city: "", user_role: "farmer", phone: "" });
  const [listings, setListings] = useState([]);
  const [listLoading, setListLoading] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    setForm({
      full_name: user.full_name || "",
      city: user.city || "",
      user_role: user.user_role || "farmer",
      phone: user.phone || "",
    });
    api
      .get("/listings/me")
      .then((r) => setListings(r.data.listings))
      .finally(() => setListLoading(false));
  }, [user, loading, navigate]);

  const save = async () => {
    await api.patch("/auth/profile", form);
    await refresh();
    setEditing(false);
  };

  const markSold = async (id) => {
    await api.patch(`/listings/${id}`, { status: "sold" });
    setListings((arr) => arr.map((l) => (l.id === id ? { ...l, status: "sold" } : l)));
  };

  const del = async (id) => {
    if (!window.confirm(t.profile.confirm_delete)) return;
    await api.delete(`/listings/${id}`);
    setListings((arr) => arr.filter((l) => l.id !== id));
  };

  if (loading || !user) return null;

  return (
    <div data-testid="profile-page" className="container-gc px-4 sm:px-6 py-8 sm:py-12">
      <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-6">{t.profile.title}</h1>

      {isWelcome && (
        <div data-testid="welcome-banner" className="card-gc p-4 mb-5 flex items-start gap-3 border-[#F59E0B]/40 bg-[#FFF8EB]">
          <Info size={20} className="text-[#D97706] mt-0.5 flex-shrink-0" />
          <p className="text-stone-800 text-sm">{t.profile.welcome}</p>
        </div>
      )}

      <div className="card-gc p-6 mb-8">
        {editing ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 pb-3 border-b border-stone-200">
              {user.profile_picture && (
                <img src={user.profile_picture} alt="" className="w-12 h-12 rounded-full" />
              )}
              <div className="min-w-0">
                <div className="text-sm text-stone-500">{t.auth.continue_google}</div>
                <div className="text-stone-900 font-medium truncate">{user.email}</div>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1.5 block">{t.auth.name_label}</label>
              <input
                data-testid="profile-name-input"
                className="input-gc"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1.5 block">{t.auth.phone_label}</label>
              <input
                data-testid="profile-phone-input"
                className="input-gc"
                placeholder="9876543210"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                inputMode="tel"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1.5 block">{t.auth.city_label}</label>
              <input
                data-testid="profile-city-input"
                className="input-gc"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1.5 block">{t.auth.role_label}</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  data-testid="profile-role-farmer-btn"
                  type="button"
                  onClick={() => setForm({ ...form, user_role: "farmer" })}
                  className={`h-12 rounded-xl border-2 font-semibold ${
                    form.user_role === "farmer"
                      ? "border-[#1E4D2B] bg-[#1E4D2B] text-white"
                      : "border-stone-300 bg-white text-stone-700"
                  }`}
                >
                  {t.auth.role_farmer}
                </button>
                <button
                  data-testid="profile-role-trader-btn"
                  type="button"
                  onClick={() => setForm({ ...form, user_role: "trader" })}
                  className={`h-12 rounded-xl border-2 font-semibold ${
                    form.user_role === "trader"
                      ? "border-[#F59E0B] bg-[#F59E0B] text-white"
                      : "border-stone-300 bg-white text-stone-700"
                  }`}
                >
                  {t.auth.role_trader}
                </button>
              </div>
            </div>
            <div className="flex gap-2">
              <button data-testid="profile-save-btn" onClick={save} className="btn-primary flex-1">
                <Save size={16} /> {t.profile.save}
              </button>
              {!isWelcome && (
                <button onClick={() => setEditing(false)} className="btn-ghost flex-1 justify-center">
                  {t.common.cancel}
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              {user.profile_picture ? (
                <img src={user.profile_picture} alt="" className="w-16 h-16 rounded-2xl object-cover flex-shrink-0" />
              ) : (
                <div className="w-16 h-16 rounded-2xl bg-[#F4F1EA] flex items-center justify-center flex-shrink-0">
                  <User size={26} className="text-[#1E4D2B]" />
                </div>
              )}
              <div className="space-y-1 min-w-0">
                <div className="text-stone-900 text-xl font-semibold truncate">{user.full_name || "—"}</div>
                <div className="flex items-center gap-2 text-stone-600 text-sm">
                  <Mail size={14} /> <span className="truncate">{user.email}</span>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-stone-600 text-sm">
                  <span className="flex items-center gap-1.5"><Phone size={14} /> {user.phone || "—"}</span>
                  <span className="flex items-center gap-1.5"><MapPin size={14} /> {user.city || "—"}</span>
                </div>
                <span className="inline-block text-xs px-2.5 py-1 rounded-full bg-[#F4F1EA] text-[#1E4D2B] font-semibold capitalize">
                  {user.user_role}
                </span>
              </div>
            </div>
            <button data-testid="profile-edit-btn" onClick={() => setEditing(true)} className="btn-ghost">
              <Edit3 size={16} /> {t.profile.edit}
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-stone-900">{t.profile.my_listings}</h2>
        <button
          data-testid="profile-add-listing-btn"
          onClick={() => navigate("/listings/new")}
          className="btn-secondary !h-10 !px-4"
        >
          <Plus size={16} /> {t.nav.sell}
        </button>
      </div>

      {listLoading ? (
        <div className="card-gc p-12 text-center text-stone-500">{t.common.loading}</div>
      ) : listings.length === 0 ? (
        <div className="card-gc p-12 text-center">
          <p className="text-stone-500 mb-4">{t.profile.no_listings}</p>
          <button onClick={() => navigate("/listings/new")} className="btn-primary">
            <Plus size={16} /> {t.profile.add_listing}
          </button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="my-listings-grid">
          {listings.map((l, idx) => (
            <div key={l.id} data-testid={`my-listing-${idx}`} className="card-gc p-4">
              <div className="flex items-start justify-between mb-2">
                <span className="text-xs px-2 py-1 rounded-full bg-[#F4F1EA] text-[#1E4D2B] font-medium">
                  {l.variety}
                </span>
                <span
                  className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${
                    l.status === "active"
                      ? "bg-green-100 text-green-800"
                      : l.status === "sold"
                      ? "bg-stone-200 text-stone-700"
                      : "bg-amber-100 text-amber-800"
                  }`}
                >
                  {l.status}
                </span>
              </div>
              <div className="text-2xl font-bold text-[#1E4D2B]">₹{l.price_per_kg}<span className="text-xs text-stone-500 font-normal">/kg</span></div>
              <div className="text-sm text-stone-600 mt-1 flex items-center gap-1">
                <Package size={14} /> {l.quantity_kg} kg
              </div>
              <div className="flex gap-2 mt-3">
                {l.status === "active" && (
                  <button
                    data-testid={`mark-sold-${idx}`}
                    onClick={() => markSold(l.id)}
                    className="btn-ghost flex-1 justify-center !h-9 text-sm"
                  >
                    <Check size={14} /> {t.profile.mark_sold}
                  </button>
                )}
                <button
                  data-testid={`delete-listing-${idx}`}
                  onClick={() => del(l.id)}
                  className="btn-ghost !h-9 text-sm !text-red-600 !border-red-200 hover:!bg-red-50"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Profile;
